import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api/client';
import { errorMessageKey } from '../../api/api-error';
import { ensureOk } from '../../auth/session';
import { AuthError, AuthLayout, AuthLinks } from '../../components/auth/auth-dialog';
import { Notice } from '../../components/ui/notice';
import type { MessageKey } from '../../i18n/en';
import { useI18n } from '../../i18n/i18n';
import { takeFragmentToken } from '../../lib/fragment-token';

/**
 * Переход по ссылке из письма, где нужен только токен: подтверждение смены email.
 * Запрос уходит сам при открытии страницы — один раз (токен одноразовый).
 */
function TokenActionPage({
  endpoint,
  title,
  done,
}: {
  endpoint: '/api/v1/auth/email/confirm-change';
  title: MessageKey;
  done: MessageKey;
}) {
  const { t } = useI18n();
  const [token] = useState(takeFragmentToken);
  const action = useMutation({
    mutationFn: async (value: string) => {
      ensureOk(await api.POST(endpoint, { body: { token: value } }));
    },
  });
  const { mutate } = action;
  // StrictMode вызывает эффект дважды; второй запрос с тем же токеном вернул бы «токен использован».
  const started = useRef(false);

  useEffect(() => {
    if (token === null || started.current) return;
    started.current = true;
    mutate(token);
  }, [token, mutate]);

  return (
    <AuthLayout title={t(title)}>
      {token === null && <AuthError>{t('error.AUTH_TOKEN_INVALID')}</AuthError>}
      {action.isPending && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
      {action.isSuccess && <Notice tone="success">{t(done)}</Notice>}
      {action.isError && <AuthError>{t(errorMessageKey(action.error))}</AuthError>}
      <AuthLinks>
        <Link to="/login">{t('auth.toLogin')}</Link>
      </AuthLinks>
    </AuthLayout>
  );
}

export function ConfirmEmailPage() {
  return (
    <TokenActionPage
      endpoint="/api/v1/auth/email/confirm-change"
      title="auth.confirmEmail.title"
      done="auth.confirmEmail.done"
    />
  );
}
